import { getConfig } from '../config';
import axiosInstance from '../axios';
import { APIResponse } from '~/services/types';
import {
  CreateFreezePayload,
  Membership,
  RemoveSessionsPayload,
  SessionHistoryResponse,
  SessionPack,
  UpdateFreezePayload,
} from '~/services/types/membership';

export const getMembershipsV2 = ({
  locationId,
  memberId,
}: {
  locationId: number;
  memberId: number;
}): Promise<Membership[]> => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<Membership[]>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships`)
    .then(({ data }) => data?.data);
};

export const getMembershipV2 = ({
  locationId,
  memberId,
  membershipId,
}: {
  locationId: number;
  memberId: number;
  membershipId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<Membership>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships/${membershipId}`)
    .then(({ data }) => data?.data);
};

export const createMembershipV2 = (
  { locationId, memberId }: { locationId: number; memberId: number },
  membership: any,
) => {
  const config = getConfig();

  const { pmId, ...membershipData } = membership;

  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships?process=true${pmId ? `&payment_method_id=${pmId}` : ''}`,
    (({ discount_is_percentage, duration, payment_amount, ...clean }) => clean)(membershipData),
  );
};

export const updateMembershipV2 = <TPayload>(
  { locationId, memberId, membershipId }: { locationId: number; memberId: number; membershipId: number },
  membership: TPayload,
) => {
  const config = getConfig();

  return axiosInstance
    .put<APIResponse>(
      `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships/${membershipId}`,
      membership,
    )
    .then(({ data }) => data);
};

export function updateFreeze(freeze: UpdateFreezePayload) {
  const config = getConfig();

  return axiosInstance
    .put<APIResponse>(
      `${config.domains.membersV2}/members/locations/${freeze.location_id}/members/${freeze.member_id}/memberships/${freeze.membership_id}/freezes/${freeze.freeze_id}`,
      freeze,
    )
    .then((r) => r.data.status);
}

export function createFreeze(freeze: CreateFreezePayload) {
  const config = getConfig();

  return axiosInstance
    .put<APIResponse>(
      `${config.domains.membersV2}/members/locations/${freeze.location_id}/members/${freeze.member_id}/memberships/${freeze.membership_id}`,
      freeze,
    )
    .then((r) => r.data.status);
}

export const addSessionsV2 = (
  { locationId, memberId, membershipId }: { locationId: number; memberId: number; membershipId: number },
  sessions: any,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships/${membershipId}/sessions`,
    sessions,
  );
};

export const getMembershipSessionPack = ({
  locationId,
  memberId,
  membershipId,
}: {
  locationId: number;
  memberId: number;
  membershipId: number;
}) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<SessionPack>
    >(`${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships/${membershipId}/sessions`)
    .then(({ data }) => data?.data);
};

export const removeSessionsV2 = (
  { locationId, memberId, membershipId }: { locationId: number; memberId: number; membershipId: number },
  payload: RemoveSessionsPayload,
) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(
    `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships/${membershipId}/sessions`,
    payload,
  );
};

export const getSessionHistory = ({
  locationId,
  memberId,
  membershipId,
}: {
  locationId: number;
  memberId: number;
  membershipId: number;
}): Promise<SessionHistoryResponse> => {
  const config = getConfig();

  return axiosInstance
    .get<SessionHistoryResponse>(
      `${config.domains.membersV2}/members/locations/${locationId}/members/${memberId}/memberships/${membershipId}/session-history`,
    )
    .then(({ data }) => data);
};
